/**
 * Event update helpers.
 *
 * Feature: events-cancel-notification-duration-alarm (U2)
 * Requirements: FR-8 (store state integrity), FR-9 (notification error surfacing)
 *
 * Background
 * ----------
 * `PUT /events` (events-update.py) returns only `{ message: 'Event updated
 * successfully' }` -- it does NOT return the updated event. But
 * `rest-client.updateEvent` declares `Promise<Event>` and previously the store
 * wrote that response body into `events[]` and `selectedEvent` AS IF it were the
 * event. That wiped `event_status`, so `event.event_status === 'canceled'`
 * evaluated `undefined === 'canceled'` -> false, and the Cancel button
 * reappeared immediately after a successful cancellation.
 *
 * TypeScript could not catch it: the contract was asserted by a generic
 * (`apiClient.put<Event>`), never validated at runtime.
 *
 * The fix is to stop depending on the response body at all -- merge the fields we
 * actually sent into the event we already hold.
 */

import type { Event, UpdateEventDto } from '../api/rest-client';

/** The single app-wide cancelled sentinel. */
export const CANCELED_STATUS = 'canceled';

/**
 * Apply an update DTO optimistically onto a cached event.
 *
 * Returns a new object; neither argument is mutated.
 *
 * Key semantics (this detail matters):
 *   - A key ABSENT from the DTO is left untouched on the result.
 *   - A key PRESENT in the DTO is applied, INCLUDING when its value is
 *     `undefined`. That is deliberate: `UpdateEventDto` carries
 *     `trail_configuration_id: undefined` to REMOVE the field, so treating
 *     present-but-undefined as "absent" would silently break removals.
 *
 * Correctness here is independent of what the API returns, which is the whole
 * point -- a backend that returns a message instead of an entity can no longer
 * corrupt client state. (BR-31, BR-32, Property P-7.)
 */
export function mergeEventUpdate(existing: Event, dto: UpdateEventDto): Event {
  return { ...existing, ...dto } as Event;
}

/**
 * Is this event cancelled?
 *
 * `true` ONLY for exactly `'canceled'`. Missing, `null`, `undefined`, an empty
 * string, a different casing, or any other value all mean ACTIVE -- matching the
 * app-wide convention that an absent `event_status` defaults to active.
 *
 * Previously this expression was inlined in three separate pages. Centralising it
 * means the "null/undefined treated as active" rule exists in one place.
 * (BR-34, Property P-8.)
 */
export function isEventCanceled(
  event: Pick<Event, 'event_status'> | null | undefined
): boolean {
  return event?.event_status === CANCELED_STATUS;
}

/** Shape of the POST /events/{id}/cancel-notification response. */
export interface CancelNotificationResult {
  message?: string;
  /** Present and true when the backend detected a duplicate and sent nothing. */
  skipped?: boolean;
  reason?: string;
  emails_sent?: number;
  email?: {
    emails_sent?: number;
    errors?: number;
    total_users?: number;
  };
  groupme?: {
    success?: boolean;
    message?: string;
    error?: string;
  };
}

export interface NotificationOutcome {
  severity: 'none' | 'warning';
  /** `null` means "say nothing to the user". */
  message: string | null;
}

const SILENT: NotificationOutcome = { severity: 'none', message: null };

/**
 * Decide what (if anything) to tell the user about a notification response.
 *
 * Rules:
 *   - `skipped: true` -> SILENT. A duplicate cancellation is a successful no-op,
 *     not a failure. Reporting it as an error would train admins to ignore real
 *     warnings. (BR-37, FR-9.3)
 *   - `errors > 0` -> warn, using the REAL counts from the response, never
 *     assumed or hardcoded values. (BR-39, FR-9.2)
 *   - anything else -> SILENT. The caller has already shown an immediate
 *     "event cancelled, notifications are being sent" acknowledgement, so a
 *     second interruption on the happy path adds noise, not information.
 *
 * Deliberately tolerant of a missing or partial `email` block: an older backend,
 * a proxy error page, or an unexpected payload must never throw inside a
 * notification handler. (SECURITY-15 style fail-safe, retained on principle even
 * though the security extension is disabled for this work item.)
 */
export function describeNotificationOutcome(
  result: CancelNotificationResult | null | undefined
): NotificationOutcome {
  if (!result || typeof result !== 'object') return SILENT;

  if (result.skipped === true) return SILENT;

  const errors = Number(result.email?.errors ?? 0);
  if (!Number.isFinite(errors) || errors <= 0) return SILENT;

  const sent = Number(result.email?.emails_sent ?? 0);
  const total = Number(
    result.email?.total_users ??
      (Number.isFinite(sent) ? sent + errors : errors)
  );

  return {
    severity: 'warning',
    message:
      `The event was cancelled successfully, but ${errors} of ${total} ` +
      `notification email(s) could not be sent. ` +
      `${Number.isFinite(sent) ? sent : 0} were sent successfully. ` +
      `You may want to notify the affected riders another way.`,
  };
}

/**
 * Message for when the notification request itself failed or never resolved.
 *
 * Wording is deliberate: the cancellation ITSELF succeeded -- it is a separate,
 * fast API call that already completed before this promise was created. Only the
 * notifications are uncertain.
 *
 * It must NOT say "cancel failed". That would be false, and it would prompt the
 * admin to cancel again -- producing exactly the duplicate notification that the
 * backend guard now has to absorb. (BR-38, FR-9.1)
 */
export function describeNotificationFailure(eventName: string): string {
  return (
    `Event "${eventName}" WAS cancelled successfully.\n\n` +
    `However, the notification request could not be confirmed, so some riders ` +
    `may not have received an email. The event is cancelled either way -- ` +
    `do not cancel it again, as that will not resend notifications.`
  );
}

// =============================================================================
// Event edit options: RSVP reset / update-notification email
// Feature: event-edit-rsvp-reset-update-email (U3)
// Requirements: FR-1.4, FR-1.5, FR-1.9, FR-2.5, FR-6.3
// =============================================================================

/** The two independent-in-mechanism choices offered on an event edit. */
export interface EventUpdateOptions {
  resetRsvps: boolean;
  sendEmail: boolean;
}

/**
 * Resolve the dialog's raw checkbox state into the actions to perform.
 *
 * Encodes the ONE-WAY coupling: choosing a reset always sends the email, but
 * the email may be sent without a reset.
 *
 * The reason is not cosmetic. "No response" is represented by the absence of a
 * rider from the attendance map, so a reset puts every rider back into the
 * un-responded state. On an event happening TODAY the daily RSVP reminder has
 * already run and will not run again, so if no email goes out nothing ever
 * re-prompts anybody -- the expected-attendance count would drop to zero and
 * stay there, which is strictly worse than the stale count the admin started
 * with. (Property P-3.)
 *
 * Exported as a pure function so the coupling is property-tested rather than
 * only enforced by a disabled checkbox in the UI.
 */
export function resolveEventUpdateOptions(
  resetRsvps: boolean,
  sendEmailChecked: boolean
): EventUpdateOptions {
  return {
    resetRsvps,
    // A reset forces the email. Never the other way round.
    sendEmail: resetRsvps ? true : sendEmailChecked,
  };
}

// -----------------------------------------------------------------------------
// !! DO NOT CHANGE THIS BOUNDARY TO event_endtime !!
//
// Eligibility to reset RSVPs is decided by the event's START time. The backend
// enforces the same rule (see is_future_event in lambda-functions/
// events-update.py, which carries the full explanation), and this check exists
// so the UI does not offer an action the server will refuse.
//
// Why start time and not end time: leaderboard-get.py awards each rider
// RSVP-accuracy credit for an event as soon as it has STARTED, by comparing
// event_expectedAttendance against event_checkin. An event that has merely
// started therefore already holds scoring data, and clearing its attendance map
// zeroes every rider's credit for it. There is no RSVP history in this system
// and no undo, so the loss is permanent.
//
// Every end-time-based boundary admits events that already hold credit:
//     event_endtime > now   -> admits IN-PROGRESS events
//   "not historical + grace" -> admits in-progress AND recently finished events
// Both are forbidden. The failure is silent: riders simply lose points.
//
// This comment is duplicated from the backend deliberately. A developer editing
// this file will not have the Python source in front of them.
// -----------------------------------------------------------------------------

/**
 * Should the edit flow offer the reset / notify options for this event?
 *
 * True only when the event has NOT started yet and is not cancelled.
 *
 * Fails CLOSED: a missing, non-string or unparseable start time returns false,
 * so a corrupt record can never unlock a destructive action. (NFR-7)
 *
 * @param event  the event being edited (or null while it is still loading)
 * @param now    reference instant; injected so the behaviour is testable
 */
export function canPromptEventUpdateOptions(
  event: Pick<Event, 'event_starttime' | 'event_status'> | null | undefined,
  now: Date
): boolean {
  if (!event) return false;
  if (isEventCanceled(event)) return false;

  const startTime = event.event_starttime;
  if (!startTime || typeof startTime !== 'string') return false;

  const startMs = new Date(startTime).getTime();
  if (!Number.isFinite(startMs)) return false;

  const nowMs = now instanceof Date ? now.getTime() : NaN;
  if (!Number.isFinite(nowMs)) return false;

  return startMs > nowMs;
}

/**
 * Count how many riders have recorded an RSVP for this event.
 *
 * Used to tell the admin exactly what a reset will destroy. "This will clear 69
 * RSVP responses" is information; "this cannot be undone" on its own is
 * decoration. (FR-1.9)
 *
 * Tolerates every shape `event_expectedAttendance` occurs in -- native object,
 * legacy JSON string, empty string, absent -- mirroring the backend's own
 * tolerance. Never throws: a count is a warning aid, and failing to render the
 * dialog would be a far worse outcome than showing zero.
 */
export function countRsvpResponses(
  event: Pick<Event, 'event_expectedAttendance'> | null | undefined
): number {
  const raw = event?.event_expectedAttendance;
  if (raw === null || raw === undefined) return 0;

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return 0;
    try {
      const parsed = JSON.parse(trimmed);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? Object.keys(parsed).length
        : 0;
    } catch {
      return 0;
    }
  }

  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return Object.keys(raw).length;
  }

  return 0;
}

// -----------------------------------------------------------------------------
// Update-notification outcome messaging
//
// !! These are SIBLINGS of describeNotificationOutcome / describeNotification-
// Failure above, NOT replacements, and they are deliberately not reuses. !!
//
// Two reasons the cancel helpers cannot be reused here:
//
//   1. SHAPE. POST /events/{id}/cancel-notification returns the counts NESTED
//      under `email`. POST /events/{id}/update-notification returns them FLAT:
//      { emails_sent, errors, total_users }. Passing a flat payload to
//      describeNotificationOutcome reads `result.email?.errors` -> undefined ->
//      Number(undefined ?? 0) -> 0 -> "no errors" -> SILENT. Every failure,
//      including a 100% failure, would be reported to the admin as success.
//
//      That is not an ordinary bug. The product decision for this feature was
//      to accept the delivery-failure path rather than build retry machinery,
//      ON THE BASIS that the admin would at least be told when it happened.
//      Reusing the cancel helper would have removed exactly that mitigation.
//
//   2. COPY. describeNotificationFailure says "WAS cancelled successfully" and
//      "do not cancel it again". Shown after an EDIT, it tells the admin their
//      event was cancelled.
//
// The cancel helpers are left untouched: the cancel endpoint still returns the
// nested shape and its callers still depend on it.
// -----------------------------------------------------------------------------

/** Shape of the POST /events/{id}/update-notification response (FLAT counts). */
export interface UpdateNotificationResult {
  message?: string;
  emails_sent?: number;
  errors?: number;
  total_users?: number;
}

/**
 * Decide what (if anything) to tell the admin about an update-notification
 * response.
 *
 * Silent on success -- the admin already saw the event save and does not need a
 * second interruption. Warns, with the REAL counts from the response, when any
 * recipient failed.
 *
 * Tolerant of a missing or partial payload by design: an older backend, a proxy
 * error page or an unexpected body must never throw inside a notification
 * handler.
 */
export function describeUpdateNotificationOutcome(
  result: UpdateNotificationResult | null | undefined
): NotificationOutcome {
  if (!result || typeof result !== 'object') return SILENT;

  const errors = Number(result.errors ?? 0);
  if (!Number.isFinite(errors) || errors <= 0) return SILENT;

  const sent = Number(result.emails_sent ?? 0);
  const safeSent = Number.isFinite(sent) ? sent : 0;
  const total = Number(result.total_users ?? safeSent + errors);
  const safeTotal = Number.isFinite(total) ? total : safeSent + errors;

  return {
    severity: 'warning',
    message:
      `The event was updated successfully, but ${errors} of ${safeTotal} ` +
      `notification email(s) could not be sent. ` +
      `${safeSent} were sent successfully. ` +
      `Riders who did not receive the email may not know the details changed.`,
  };
}

/**
 * Message for when the update-notification request itself failed or never
 * resolved.
 *
 * Wording is deliberate on three points:
 *   - the UPDATE succeeded; only the notification is uncertain
 *   - it must never say "cancel", which would be false and alarming
 *   - it tells the admin that saving again will not resend, so they do not
 *     re-save repeatedly trying to trigger an email
 */
export function describeUpdateNotificationFailure(eventName: string): string {
  return (
    `Event "${eventName}" WAS updated successfully.\n\n` +
    `However, the notification email could not be confirmed, so some riders ` +
    `may not know the details changed. Saving the event again will not resend ` +
    `the notification -- contact affected riders another way if needed.`
  );
}
